import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Building2, Users, FileText, DollarSign, Wrench, HardHat,
  ShieldCheck, Landmark, Banknote, Home, Plug, Receipt, FolderArchive, Calendar as CalIcon,
  BarChart3, Bell, Bot, Settings as Cog, UserCog, ScrollText, Moon, Sun, LogOut, Menu, ChevronDown, Check, BellRing, ClipboardList, ShieldCheck as ShieldCheckIcon, MessageSquare,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const SECTIONS = [
  { heading: "Overview", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin", "manager", "readonly"] },
    { to: "/calendar", label: "Calendar", icon: CalIcon, roles: ["admin", "manager", "readonly"] },
    { to: "/alerts", label: "Alerts", icon: Bell, roles: ["admin", "manager", "readonly"] },
    { to: "/ai", label: "AI Assistant", icon: Bot, roles: ["admin", "manager", "readonly"] },
  ]},
  { heading: "Portfolio", items: [
    { to: "/properties", label: "Properties", icon: Building2, roles: ["admin", "manager", "readonly", "contractor"] },
    { to: "/tenants", label: "Tenants", icon: Users, roles: ["admin", "manager", "readonly"] },
    { to: "/leases", label: "Leases & Units", icon: FileText, roles: ["admin", "manager", "readonly"] },
    { to: "/checklists", label: "Checklists", icon: ClipboardList, roles: ["admin", "manager", "readonly"] },
    { to: "/compliance", label: "Compliance", icon: ShieldCheckIcon, roles: ["admin", "manager", "readonly"] },
    { to: "/messages", label: "Messages", icon: MessageSquare, roles: ["admin", "manager", "readonly"] },
    { to: "/payments", label: "Rent Collection", icon: DollarSign, roles: ["admin", "manager", "readonly"] },
    { to: "/zelle", label: "Zelle Payments", icon: Banknote, roles: ["admin", "manager", "readonly"] },
    { to: "/reminders", label: "Rent Reminders", icon: BellRing, roles: ["admin", "manager", "readonly"] },
    { to: "/banking", label: "Bank Transactions", icon: Landmark, roles: ["admin", "manager", "readonly"] },
  ]},
  { heading: "Operations", items: [
    { to: "/maintenance", label: "Maintenance", icon: Wrench, roles: ["admin", "manager", "readonly", "contractor"] },
    { to: "/contractors", label: "Contractors", icon: HardHat, roles: ["admin", "manager", "readonly"] },
  ]},
  { heading: "Financials", items: [
    { to: "/insurance", label: "Insurance", icon: ShieldCheck, roles: ["admin", "manager", "readonly"] },
    { to: "/taxes", label: "Property Taxes", icon: Landmark, roles: ["admin", "manager", "readonly"] },
    { to: "/mortgages", label: "Mortgages", icon: Banknote, roles: ["admin", "manager", "readonly"] },
    { to: "/hoa", label: "HOA", icon: Home, roles: ["admin", "manager", "readonly"] },
    { to: "/utilities", label: "Utilities", icon: Plug, roles: ["admin", "manager", "readonly"] },
    { to: "/expenses", label: "Expenses", icon: Receipt, roles: ["admin", "manager", "readonly"] },
  ]},
  { heading: "System", items: [
    { to: "/documents", label: "Documents", icon: FolderArchive, roles: ["admin", "manager", "readonly"] },
    { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager", "readonly"] },
    { to: "/users", label: "User Management", icon: UserCog, roles: ["admin"] },
    { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["admin"] },
    { to: "/settings", label: "Settings", icon: Cog, roles: ["admin", "manager", "readonly"] },
  ]},
];

const MODULES = [
  { name: "Real Estate Portfolio", active: true },
  { name: "Kora Finance", active: false },
  { name: "Kora CRM", active: false },
  { name: "Kora Documents", active: false },
  { name: "Kora Reports", active: false },
  { name: "Kora HR", active: false },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [dark, setDark] = useState(document.documentElement.classList.contains("dark"));
  const [open, setOpen] = useState(false);

  const toggleDark = () => { document.documentElement.classList.toggle("dark"); setDark(!dark); };
  const sections = SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => i.roles.includes(user?.role)) })).filter((s) => s.items.length);

  const [smsUnread, setSmsUnread] = useState(0);
  const [attention, setAttention] = useState({ unread_sms: 0, awaiting_confirmation: 0, failed: 0, total: 0 });
  const navigate = useNavigate();
  const prev = useRef(null);

  useEffect(() => {
    if (!user) return;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (err) { console.debug("Notification permission request failed:", err); }
    }
    let stop = false;
    const notify = (title, body, tab) => {
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      const n = new Notification(title, { body, tag: "kora-alert", icon: "/favicon.ico" });
      n.onclick = () => { window.focus(); navigate(`/messages?tab=${tab}`); n.close(); };
    };
    const poll = async () => {
      try {
        const { data } = await api.get("/messaging/attention");
        if (stop) return;
        setAttention(data);
        setSmsUnread(data.total || 0);
        const p = prev.current;
        if (p) {
          if (data.unread_sms > p.unread_sms) {
            try {
              const { data: threads } = await api.get("/sms/threads");
              const top = (threads || []).find((t) => t.unread > 0) || (threads || [])[0];
              notify("New tenant message", top ? `${top.tenant_name || top.phone}: ${top.last_body}` : "New inbound SMS", "inbox");
            } catch (e) { notify("New tenant message", "New inbound SMS", "inbox"); }
          }
          if (data.failed > p.failed) notify("Message delivery failed", "A tenant message could not be delivered.", "history");
          if (data.awaiting_confirmation > p.awaiting_confirmation) notify("Awaiting tenant confirmation", "A message is waiting for a tenant response.", "history");
        }
        prev.current = data;
      } catch (err) { console.debug("Attention poll failed:", err); }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { stop = true; clearInterval(id); };
  }, [user, navigate]);
  const badgeColor = attention.failed > 0 ? "bg-red-500" : (attention.unread_sms > 0 ? "bg-emerald-500" : "bg-amber-500");
  const badgeTitle = `${attention.unread_sms} unread · ${attention.awaiting_confirmation} awaiting · ${attention.failed} failed`;

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        className={`sidebar-dark fixed lg:static z-40 h-screen w-[260px] flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center px-5 border-b border-white/10 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center mr-2.5">
            <span className="text-white font-heading font-extrabold text-sm">K</span>
          </div>
          <div className="leading-tight">
            <p className="font-heading font-bold text-sm text-white">Kora Holding, LLC</p>
            <p className="text-[10px] text-slate-400 tracking-wide">Real Estate Portfolio</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
          {sections.map((sec) => (
            <div key={sec.heading}>
              <p className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{sec.heading}</p>
              <div className="space-y-0.5">
                {sec.items.map((n) => (
                  <NavLink
                    key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                    data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive ? "nav-active font-medium" : "text-slate-300 hover:bg-white/5 hover:text-white"
                      }`}
                  >
                    <n.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                    <span className="flex-1">{n.label}</span>
                    {n.to === "/messages" && smsUnread > 0 && (
                      <span data-testid="nav-sms-unread" title={badgeTitle} className={`min-w-[18px] h-[18px] px-1 rounded-full ${badgeColor} text-white text-[10px] font-semibold flex items-center justify-center`}>{smsUnread > 99 ? "99+" : smsUnread}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 shrink-0">
          <div className="flex items-center gap-2.5 px-1 mb-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 text-white flex items-center justify-center font-heading font-semibold text-sm">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-button" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-red-400 transition-colors">
            <LogOut className="w-4 h-4" strokeWidth={1.75} /> Sign Out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle"><Menu className="w-5 h-5" /></button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary hover:bg-secondary/70 transition-colors text-sm font-medium" data-testid="module-switcher">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> Real Estate Portfolio
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>Kora Modules</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {MODULES.map((m) => (
                  <DropdownMenuItem key={m.name} disabled={!m.active} className="flex items-center justify-between">
                    {m.name}
                    {m.active ? <Check className="w-4 h-4 text-emerald-500" /> : <span className="text-[10px] text-muted-foreground">Soon</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="attention-bell" title={attention.total > 0 ? badgeTitle : "No items need attention"} className="relative p-2 rounded-lg hover:bg-muted transition-colors">
                  <BellRing className="w-[18px] h-[18px]" strokeWidth={1.75} />
                  {attention.total > 0 && (
                    <span data-testid="attention-bell-count" className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full ${badgeColor} text-white text-[9px] font-semibold flex items-center justify-center`}>{attention.total > 99 ? "99+" : attention.total}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72" data-testid="attention-dropdown">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Needs Attention</span>
                  {attention.total > 0 && <span className="text-[10px] font-normal text-muted-foreground">{attention.total} total</span>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {attention.total === 0 ? (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground" data-testid="attention-empty">
                    <Check className="w-5 h-5 mx-auto mb-1.5 text-emerald-500" /> All clear — nothing needs attention.
                  </div>
                ) : (
                  <>
                    {[
                      { key: "unread_sms", label: "Unread tenant texts", to: "/messages?tab=inbox", dot: "bg-emerald-500", testid: "attention-item-unread" },
                      { key: "awaiting_confirmation", label: "Awaiting confirmation", to: "/messages?tab=history", dot: "bg-amber-500", testid: "attention-item-awaiting" },
                      { key: "failed", label: "Failed deliveries", to: "/messages?tab=history", dot: "bg-red-500", testid: "attention-item-failed" },
                      { key: "contractor_followups", label: "Contractor follow-ups", to: "/maintenance", dot: "bg-orange-500", testid: "attention-item-contractor" },
                    ].filter((r) => attention[r.key] > 0).map((r) => (
                      <DropdownMenuItem key={r.key} data-testid={r.testid} onClick={() => navigate(r.to)} className="flex items-center justify-between cursor-pointer">
                        <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${r.dot}`} /> {r.label}</span>
                        <span className="text-xs font-semibold tabular-nums">{attention[r.key]}</span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="attention-open-center" onClick={() => navigate("/messages")} className="text-primary cursor-pointer justify-center text-sm">
                      Open Communication Center
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={toggleDark} data-testid="theme-toggle" className="p-2 rounded-lg hover:bg-muted transition-colors">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
